import { useMemo } from 'react';
import { useActiveChats } from '~/hooks/queries/use-chats';
import { useChatStatuses } from '~/hooks/queries/use-chat-status';
import { useCodingSessionsForBoard } from '~/hooks/queries/use-coding-sessions';
import { useProBuilderSessionForBoard } from '~/hooks/queries/use-pro-builder-session';
import {
  countBarChats,
  groupBarChats,
  mapChatsToBar,
  mapCodingToBar,
  mapProBuilderToBar,
  type BarChat,
  type BarCounts,
  type BarGroup,
} from './state-mapping';

export interface UseChatActivityOptions {
  /**
   * FRONT-1681: fold Building Sessions (coding sessions) into the board
   * alongside chats. Defaults to OFF so the in-modal `ChatActivityMenu`
   * (`inbox-chat-modal.tsx`) — which also uses this hook — never renders a
   * build row: builds are deliberately top-bar-only this round. Off also
   * disables `useCodingSessionsForBoard`'s own query (not just its use in
   * the merge below), so that surface issues no ADDITIONAL network request
   * for it.
   *
   * FRONT-1681 (review round): that claim relies on
   * `useCodingSessionsForBoard`'s disabled-query fallback being a STABLE
   * reference (a module-level frozen empty array, `use-coding-sessions.ts`)
   * — a fresh `[]` literal each render would sit in `barChats`'s `useMemo`
   * deps below and force it to recompute on every render even with
   * `includeBuilds` off, handing `ChatActivityMenu` a new array reference
   * each time despite nothing having changed.
   *
   * FRONT-1681 (third review round): "stays byte-identical, network
   * footprint unchanged" (the previous wording here) overstated it —
   * `useCodingSessionsForBoard` shares its query KEY with every other reader
   * of it, so a disabled instance's `data` can still be real session rows
   * read straight out of that shared cache entry, not the frozen fallback.
   * (The always-on writer that made this routine was the `useCodingActivity`
   * poll behind the chat composer's coding icon; both went on 2026-09-01
   * with the Build button, but the coding window's own `useCodingSessions`
   * still shares the key, so this is dormant rather than gone.) It's
   * harmless here only because the merge below (`includeBuilds ?
   * mapCodingToBar(…) : []`) never calls `mapCodingToBar` on whatever
   * `useCodingSessionsForBoard` returns when builds are off — but that's a
   * property of the merge, not of the query being "disabled".
   */
  includeBuilds?: boolean;
  /**
   * FRONT-3040: fold the person's Pro Builder session (their own Claude Code)
   * in as one more row, merged the same way build rows are. Defaults to OFF,
   * and off disables the session poll itself, so `ChatActivityMenu` (which
   * passes no options) issues no request and renders no such row.
   */
  includeProBuilder?: boolean;
}

/**
 * The activity board's data wiring (FRONT-1579) — active chats + their
 * statuses, optionally folded together with Building Sessions (FRONT-1681,
 * `includeBuilds`), reduced to the board's `groups`, the cluster's `counts`,
 * and the flat `barChats` list a caller's own derived state (e.g.
 * `firstAsking` for the ask chip) reads from.
 *
 * Factored out of `chat-top-bar.tsx:46-55` so the inbox chat modal's own
 * `ChatActivityMenu` (FRONT-1579) can mount the identical board without a
 * second, hand-copied version of this wiring drifting out of sync with the
 * top bar's. Deliberately narrow: approvals + the ask chip stay in
 * `chat-top-bar.tsx`, the only surface that renders an ask chip.
 */
export function useChatActivity(
  options: UseChatActivityOptions = {},
): { barChats: BarChat[]; groups: BarGroup[]; counts: BarCounts } {
  const { includeBuilds = false, includeProBuilder = false } = options;
  const { data: chats = [] } = useActiveChats();
  const sessionIds = useMemo(() => chats.map((c) => c.sessionId), [chats]);
  const { data: statuses = {} } = useChatStatuses(sessionIds);
  const buildSessions = useCodingSessionsForBoard(includeBuilds);
  const proBuilderSession = useProBuilderSessionForBoard(includeProBuilder);

  const barChats = useMemo(() => {
    const chatRows = mapChatsToBar(chats, statuses);
    return [
      ...chatRows,
      ...(includeBuilds ? mapCodingToBar(buildSessions) : []),
      ...(includeProBuilder ? mapProBuilderToBar(proBuilderSession) : []),
    ];
  }, [chats, statuses, includeBuilds, buildSessions, includeProBuilder, proBuilderSession]);
  const counts = useMemo(() => countBarChats(barChats), [barChats]);
  const groups = useMemo(() => groupBarChats(barChats), [barChats]);

  return { barChats, groups, counts };
}
